import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ShieldCheck, XCircle } from "lucide-react";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";

/**
 * OAuth callback landing page.
 *
 * Emergent redirects here with `#session_id=<...>`. We synchronously grab it
 * during first render (useRef guards against double-execution under StrictMode)
 * and exchange it for our own JWT via POST /api/auth/google/session.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("Verificando con Google…");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const parseSessionId = () => {
      const hash = window.location.hash || "";
      const search = window.location.search || "";
      const h = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
      const s = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
      return h.get("session_id") || s.get("session_id");
    };

    const sessionId = parseSessionId();
    if (!sessionId) {
      setError("No recibimos una sesión válida de Google. Vuelve a intentarlo.");
      return;
    }

    // Clean the URL BEFORE the API call so that a refresh or hit-back cannot
    // re-submit the same (single-use) session_id and cause a 404 loop.
    window.history.replaceState(null, "", "/auth/callback");

    (async () => {
      try {
        setStatus("Iniciando tu sesión…");
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        login(data.access_token, data.user);

        if (data.user.pending_role_selection) {
          navigate("/auth/rol", { replace: true });
          return;
        }
        const target =
          data.user.role === "admin"
            ? "/admin"
            : data.user.role === "medico"
            ? "/medico"
            : "/dashboard";
        navigate(target, { replace: true });
      } catch (e) {
        // Prefer server detail; fallback for a raw 404 (no JSON body) to a
        // clear Spanish message so the user isn't shown "Request failed…".
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (typeof detail === "string" && detail.length > 0) {
          setError(detail);
        } else if (status === 404 || status === 401) {
          setError("Tu sesión de Google expiró o ya fue consumida. Vuelve a iniciar sesión con Google.");
        } else {
          setError(formatApiError(e));
        }
      }
    })();
  }, [login, navigate, location]);

  if (error) {
    return (
      <Shell>
        <div className="text-center max-w-sm mx-auto" data-testid="google-callback-error">
          <div className="w-12 h-12 mx-auto rounded-full border border-destructive/40 bg-destructive/5 grid place-items-center">
            <XCircle className="text-destructive" size={22} />
          </div>
          <h1 className="mt-4 font-display text-xl font-semibold text-[#0F2942]">
            No pudimos verificar tu sesión de Google
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
            <Button
              onClick={() => {
                const redirectUrl = window.location.origin + "/auth/callback";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
              data-testid="google-callback-retry-btn"
            >
              Reintentar con Google
            </Button>
            <Button
              onClick={() => navigate("/login", { replace: true })}
              variant="outline"
              data-testid="google-callback-back-login-btn"
            >
              Ir al inicio de sesión
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="text-center max-w-sm mx-auto" data-testid="google-callback-loading">
        <div className="w-12 h-12 mx-auto rounded-full border border-border bg-white grid place-items-center">
          <ShieldCheck className="text-[#2563EB]" size={22} />
        </div>
        <h1 className="mt-4 font-display text-xl font-semibold text-[#0F2942]">
          Autenticando con Google
        </h1>
        <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> {status}
        </p>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-12">{children}</main>
      <Footer />
    </div>
  );
}
